/**
 * Camera-following cull sphere: while enabled, hides the point cloud within
 * `radius` of the active camera via the material clip spheres (SHOW_OUTSIDE),
 * so the user can work from inside the scan without nearby points occluding
 * the view. The shader does the actual sphere test in view space each frame;
 * the per-tick listener here only repositions the sphere shim (one matrix
 * invert) and covers point clouds that finish loading late.
 *
 * Shared by the floor tools: ManualFloorTool keeps it on (settings-driven)
 * while placing floor/ceiling planes; the floor preview edit rig hosts turn
 * it on during a face repick, replacing the old camera-sector half-box cut.
 */

import * as THREE from 'three';
import { Viewer } from '../../../viewer/viewer';
import { ClipTask } from '../../../defines';

export const DEFAULT_CAMERA_CULL_RADIUS_M = 5;

export class CameraCullSphere {
  private readonly shim = Object.assign(new THREE.Object3D(), {
    getActualClipTask: () => ClipTask.SHOW_OUTSIDE,
  });
  private listening = false;

  /** Cull radius in meters; takes effect on the next tick. */
  radius = DEFAULT_CAMERA_CULL_RADIUS_M;

  constructor(private readonly viewer: Viewer) {}

  isEnabled(): boolean {
    return this.listening;
  }

  enable(): void {
    if (this.listening) return;
    this.listening = true;
    this.viewer.addEventListener('update', this.tick);
    this.tick();
  }

  disable(): void {
    if (!this.listening) return;
    this.listening = false;
    this.viewer.removeEventListener('update', this.tick);
    for (const pointcloud of this.viewer.scene.pointclouds) {
      pointcloud.material.setClipSpheres([]);
    }
  }

  private readonly tick = (): void => {
    if (this.radius <= 0) return;
    this.shim.position.copy(this.viewer.scene.getActiveCamera().position);
    this.shim.scale.setScalar(this.radius);
    this.shim.updateMatrixWorld(true);
    const spheres = [{ inverse: this.shim.matrixWorld.clone().invert(), sphere: this.shim }];
    for (const pointcloud of this.viewer.scene.pointclouds) {
      pointcloud.material.setClipSpheres(spheres);
    }
  };
}
